import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const UnderstandingAnalytics: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Creator Tips • August 2, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          Analytics: Understanding Your Audience
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Creating content without analyzing performance is like driving blindfolded. IndeeClips analytics tells you exactly what's working, who's watching, and how to grow faster. This guide demystifies the most important metrics and shows you how to use data to make smarter content decisions that accelerate your channel growth.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          The Metrics That Actually Matter
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          1. Watch Time (Your #1 Priority)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Watch time is the total minutes people spend watching your videos. It's the single most important metric because IndeeClips' algorithm prioritizes videos that keep people on the platform longer.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Why it matters:</strong> High watch time signals quality content—the algorithm promotes it to more viewers</li>
          <li><strong>How to improve:</strong> Create longer videos (8-15 minutes), hook viewers early, maintain pacing, add chapters to keep viewers engaged</li>
          <li><strong>Goal:</strong> Aim for 50%+ average view duration on most videos</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          2. Click-Through Rate (CTR)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          CTR is the percentage of people who see your thumbnail and click to watch. It measures how compelling your title and thumbnail are.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Good CTR:</strong> 4-6% for most creators, 8-12% for top creators</li>
          <li><strong>How to improve:</strong> A/B test thumbnails, write curiosity-driven titles, use bold colors and expressive faces</li>
          <li><strong>Red flag:</strong> CTR below 2% means your packaging (title/thumbnail) needs work</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          3. Average View Duration (AVD)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          AVD tells you how long viewers watch before clicking away. It's watch time divided by total views, expressed as a percentage or time.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Interpretation:</strong> 50% AVD on a 10-minute video = viewers watch 5 minutes on average</li>
          <li><strong>How to improve:</strong> Cut boring intros, front-load value, add pattern interrupts every 30-60 seconds</li>
          <li><strong>Audience retention graph:</strong> Shows exactly where viewers drop off—fix those weak points</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          4. Subscriber Growth Rate
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          How many new subscribers you gain per 1,000 views. Healthy channels convert 1-3% of viewers into subscribers.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Low conversion?</strong> You're attracting the wrong audience or not asking for subscribers</li>
          <li><strong>How to improve:</strong> Create series content, ask viewers to subscribe if they want more, deliver consistent value</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Understanding Traffic Sources
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Where Your Views Come From
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Browse Features (Homepage):</strong> IndeeClips recommending your video to users—great for discovery</li>
          <li><strong>Suggested Videos:</strong> Shown alongside/after other videos—indicates your content is related to popular topics</li>
          <li><strong>Search:</strong> People finding you via keywords—good for evergreen content</li>
          <li><strong>Direct/External:</strong> Links from social media, websites, or direct URL visits</li>
          <li><strong>Channel Pages:</strong> Existing subscribers watching from your channel—loyal audience indicator</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          What Each Source Means
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          High browse/suggested traffic = algorithm trusts your content. High search traffic = strong SEO. High external traffic = you're promoting well outside IndeeClips. Balanced mix is healthiest.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Audience Demographics: Know Your Viewers
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Age and Gender
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Knowing whether you reach 18-24 year olds or 35-44 year olds changes your content style, references, and tone. Tailor content to your actual audience, not who you think watches.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Geography
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If 80% of viewers are from specific Indian states, create content relevant to those regions. Include local references, language, and culturally relevant examples.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Watch Time by Video
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Your top 10 videos by watch time reveal what your audience actually wants. Double down on those topics. Your most-viewed video isn't always your best—watch time matters more than raw views.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Using Analytics to Make Decisions
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Identify Your Best Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Sort videos by watch time (not views). Your top performers reveal patterns—similar topics, formats, or styles. Create more of what works.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Fix Underperforming Videos
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Low CTR + high AVD:</strong> Great content, bad packaging—change thumbnail/title</li>
          <li><strong>High CTR + low AVD:</strong> Clickbait that disappoints—make content match promise</li>
          <li><strong>Low CTR + low AVD:</strong> Topic doesn't resonate—move on to something else</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Optimize Upload Schedule
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Check "When your viewers are on IndeeClips" in analytics. Upload 1-2 hours before peak times so the algorithm has time to test your video with subscribers before the rush.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Advanced Analytics Strategies
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Retention Curve
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Study your audience retention graph. Where do most people drop off? Fix that section in future videos. Peaks in the curve show what content hooks viewers—do more of that.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Subscriber Source Analysis
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Which videos convert viewers to subscribers best? Those are your "gateway videos"—promote them externally and in end screens to grow faster.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Returning Viewers vs. New Viewers
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Healthy channels have 30-50% returning viewers. If you're below 20%, focus on building loyalty (series, consistent uploads, community engagement). Above 70%? You're preaching to the choir—need better discovery.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Common Analytics Mistakes
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Obsessing over views:</strong> 10,000 views with 2% AVD is worse than 1,000 views with 80% AVD</li>
          <li><strong>Ignoring small sample sizes:</strong> Don't panic if a video tanks in the first hour—give it 48 hours minimum</li>
          <li><strong>Chasing every trend:</strong> Analytics show what YOUR audience wants—not what's viral elsewhere</li>
          <li><strong>Not tracking progress:</strong> Screenshot key metrics monthly to see long-term growth patterns</li>
          <li><strong>Paralysis by analysis:</strong> Data informs, but you still need to create—don't overthink every number</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Your Weekly Analytics Routine
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Monday:</strong> Review last week's uploads—which performed best and why?</li>
          <li><strong>Wednesday:</strong> Check traffic sources—are you growing in browse/suggested?</li>
          <li><strong>Friday:</strong> Study top 5 videos of all time—what patterns emerge?</li>
          <li><strong>Monthly:</strong> Deep dive into demographics—is your audience shifting?</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Let Data Guide Your Growth
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Analytics isn't about proving you're successful—it's about discovering what to do next. The creators who grow fastest aren't the most talented; they're the ones who listen to their data and adapt. Check your analytics weekly, identify one thing to improve, and test that improvement in your next video.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Every video is an experiment. Every metric is feedback. Use it. Questions about interpreting your analytics? Email{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default UnderstandingAnalytics;
